"use client";

// HeaderIconsWidget — Renders user, locale, currency, wishlist, cart icons.
// On mobile (<md), search/user/wishlist/cart move to the fixed MobileBottomBar.
// The locale toggle stays visible in the header (one tap to switch language)
// and is also mirrored in the nav drawer alongside the currency switcher.
import { c as _c } from "react/compiler-runtime";
import { MagnifyingGlass, Package, SignOut, UserCircle, User as UserIcon } from "@phosphor-icons/react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import LocaleSwitcher from "@/components/layout/LocaleSwitcher";
import CartIcon from "@/components/storefront/CartIcon";
import CurrencySwitcher from "@/components/storefront/CurrencySwitcher";
import { ThemeToggle } from "@/components/storefront/ThemeToggle";
import WishlistIcon from "@/components/storefront/WishlistIcon";
import { usePageIdle } from "@/hooks/use-page-idle";
import { signOutAction } from "@/lib/auth/sign-out-action";
import { useSessionUser } from "@/lib/auth/use-session-user";
import { useWishlistEnabled } from "@/lib/storefront/wishlist-enabled";
import type { HeaderIconsConfig } from "@/lib/structured-builder/widget-types/header-icons";
import { Skeleton } from '@/components/storefront/ui/skeleton';
const StorefrontSearchOverlay = dynamic(() => import("@/components/storefront/StorefrontSearchOverlay"), {
  ssr: false
});
type Props = {
  config: HeaderIconsConfig;
  locale: string;
};
export function HeaderIconsWidget(t0) {
  const $ = _c(32);
  const {
    config,
    locale
  } = t0;
  const iconSize = config.icon_size === "sm" ? 20 : config.icon_size === "lg" ? 24 : 22;
  const wishlistEnabled = useWishlistEnabled();
  const [searchOpen, setSearchOpen] = useState(false);
  const pageIdle = usePageIdle();
  const isRTL = locale === "ar";
  let t1;
  if ($[0] !== config.show_search || $[1] !== iconSize || $[2] !== isRTL || $[3] !== pageIdle || $[4] !== searchOpen) {
    t1 = config.show_search && <><button type="button" onClick={() => setSearchOpen(true)} className="hidden md:inline-flex items-center justify-center w-10 h-10 rounded-lg text-foreground hover:text-primary hover:bg-accent transition-colors" aria-label={isRTL ? "\u0628\u062D\u062B" : "Search"}><MagnifyingGlass size={iconSize} /></button>{(pageIdle || searchOpen) && <StorefrontSearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />}</>;
    $[0] = config.show_search;
    $[1] = iconSize;
    $[2] = isRTL;
    $[3] = pageIdle;
    $[4] = searchOpen;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== config.show_user || $[7] !== iconSize || $[8] !== locale) {
    t2 = config.show_user && <span className="hidden md:inline-flex"><UserSlot iconSize={iconSize} locale={locale} /></span>;
    $[6] = config.show_user;
    $[7] = iconSize;
    $[8] = locale;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== config.show_locale || $[11] !== locale) {
    t3 = config.show_locale && <span className="inline-flex"><LocaleSwitcher locale={locale} /></span>;
    $[10] = config.show_locale;
    $[11] = locale;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== config.show_theme_toggle || $[14] !== iconSize || $[15] !== locale) {
    t4 = config.show_theme_toggle && <ThemeToggle initial={null} locale={locale} size={iconSize} />;
    $[13] = config.show_theme_toggle;
    $[14] = iconSize;
    $[15] = locale;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== config.show_currency) {
    t5 = config.show_currency && <span className="hidden sm:inline-flex"><CurrencySwitcher /></span>;
    $[17] = config.show_currency;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== config.show_wishlist || $[20] !== wishlistEnabled) {
    t6 = config.show_wishlist && wishlistEnabled && <span className="hidden md:inline-flex"><WishlistIcon /></span>;
    $[19] = config.show_wishlist;
    $[20] = wishlistEnabled;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== config.show_cart) {
    t7 = config.show_cart && <span className="hidden md:inline-flex"><CartIcon /></span>;
    $[22] = config.show_cart;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t1 || $[25] !== t2 || $[26] !== t3 || $[27] !== t4 || $[28] !== t5 || $[29] !== t6 || $[30] !== t7) {
    t8 = <div className="flex items-center gap-2 flex-shrink-0 [&>*]:flex-shrink-0 [&>*]:h-10">{t1}{t2}{t3}{t4}{t5}{t6}{t7}</div>;
    $[24] = t1;
    $[25] = t2;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  return t8;
}

// ─── User slot ────────────────────────────────────────────────────
// Logged out: simple link to /login.
// Logged in: avatar with display-name initial, opening a small menu
// with profile / orders / sign out.

function UserSlot(t0) {
  const $ = _c(10);
  const {
    iconSize,
    locale
  } = t0;
  const {
    user,
    loading
  } = useSessionUser();
  const isRTL = locale === "ar";
  if (loading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Skeleton className="inline-block w-10 h-10 rounded-full" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (!user) {
    const t1 = `/${locale}/login`;
    const t2 = isRTL ? "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644" : "Log in";
    let t3;
    if ($[1] !== iconSize) {
      t3 = <UserCircle size={iconSize} />;
      $[1] = iconSize;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
      t4 = <Link href={t1} className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-foreground hover:text-primary transition-colors" aria-label={t2}>{t3}</Link>;
      $[3] = t1;
      $[4] = t2;
      $[5] = t3;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  let t1;
  if ($[7] !== locale || $[8] !== user) {
    t1 = <UserMenu user={user} locale={locale} />;
    $[7] = locale;
    $[8] = user;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
}
function initialFor(user: {
  email?: string | null;
  user_metadata?: Record<string, unknown>;
}) {
  const meta = user.user_metadata ?? {};
  const fullName = meta.full_name as string | undefined ?? meta.name as string | undefined;
  const source = fullName?.trim() || user.email?.trim() || "";
  return source.charAt(0).toUpperCase() || "?";
}
function UserMenu(t0) {
  const $ = _c(27);
  const {
    user,
    locale
  } = t0;
  const t = useTranslations("auth");
  const isRTL = locale === "ar";
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  let t1;
  let t2;
  if ($[0] !== open) {
    t1 = () => {
      if (!open) {
        return;
      }
      const onClick = function onClick(e) {
        if (ref.current && !ref.current.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      const onKey = function onKey(e_0) {
        if (e_0.key === "Escape") {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", onClick);
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("mousedown", onClick);
        document.removeEventListener("keydown", onKey);
      };
    };
    t2 = [open];
    $[0] = open;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== locale) {
    t3 = async function handleSignOut() {
      await signOutAction();
      window.location.href = `/${locale}`;
    };
    $[3] = locale;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleSignOut = t3;
  let t4;
  if ($[5] !== user.user_metadata) {
    t4 = user.user_metadata ?? {};
    $[5] = user.user_metadata;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const meta = t4;
  const displayName = meta.full_name as string | undefined ?? meta.name as string | undefined ?? user.email ?? "";
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setOpen(_temp);
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = isRTL ? "\u062D\u0633\u0627\u0628\u064A" : "My account";
  let t7;
  if ($[8] !== user) {
    t7 = initialFor(user);
    $[8] = user;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7) {
    t8 = <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary text-[11px] font-semibold border border-primary/20">{t7}</span>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== open || $[13] !== t6 || $[14] !== t8) {
    t9 = <button type="button" onClick={t5} className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-foreground hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-haspopup="menu" aria-expanded={open} aria-label={t6}>{t8}</button>;
    $[12] = open;
    $[13] = t6;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== displayName || $[17] !== handleSignOut || $[18] !== isRTL || $[19] !== locale || $[20] !== open || $[21] !== t || $[22] !== user.email) {
    t10 = open && <div className="absolute end-0 top-full mt-2 min-w-[220px] rounded-xl border border-border bg-background shadow-lg z-30 overflow-hidden"><div className="px-3 py-2.5 border-b border-border"><p className="truncate text-sm font-medium text-foreground">{displayName}</p>{user.email && displayName !== user.email && <p className="truncate text-xs text-muted-foreground">{user.email}</p>}</div><div className="py-1"><Link href={`/${locale}/account/profile`} onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"><UserIcon size={16} className="opacity-70" />{t("myProfile")}</Link><Link href={`/${locale}/account/orders`} onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"><Package size={16} className="opacity-70" />{isRTL ? "\u0637\u0644\u0628\u0627\u062A\u064A" : "My Orders"}</Link></div><div className="border-t border-border py-1"><button type="button" onClick={() => {
          setOpen(false);
          handleSignOut();
        }} className="flex items-center gap-2 w-full text-start px-3 py-2 text-sm text-danger hover:bg-danger/10 transition-colors"><SignOut size={16} className="opacity-70" />{t("logout")}</button></div></div>;
    $[16] = displayName;
    $[17] = handleSignOut;
    $[18] = isRTL;
    $[19] = locale;
    $[20] = open;
    $[21] = t;
    $[22] = user.email;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t9) {
    t11 = <div ref={ref} className="relative">{t9}{t10}</div>;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}
function _temp(v) {
  return !v;
}